/**
 * Inline icons, stroke based, 20px grid. Bundling an icon package for eleven
 * glyphs costs more than it saves.
 */

type Props = { className?: string };

function svg(path: React.ReactNode) {
  return function Icon({ className = "h-5 w-5" }: Props) {
    return (
      <svg
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
      >
        {path}
      </svg>
    );
  };
}

export const PlayIcon = svg(<path d="M6 4.5v11l9-5.5-9-5.5z" fill="currentColor" stroke="none" />);
export const PauseIcon = svg(
  <>
    <path d="M7 4v12" />
    <path d="M13 4v12" />
  </>,
);
export const BackIcon = svg(
  <>
    <path d="M4 10a6 6 0 1 1 2 4.5" />
    <path d="M4 6v4h4" />
  </>,
);
export const ForwardIcon = svg(
  <>
    <path d="M16 10a6 6 0 1 0-2 4.5" />
    <path d="M16 6v4h-4" />
  </>,
);
export const TextIcon = svg(
  <>
    <path d="M4 5h12" />
    <path d="M4 9h12" />
    <path d="M4 13h8" />
  </>,
);
export const PageIcon = svg(
  <>
    <rect x="4" y="3" width="12" height="14" rx="1.5" />
    <path d="M7 7h6" />
    <rect x="7" y="10" width="6" height="4" />
  </>,
);
export const SearchIcon = svg(
  <>
    <circle cx="9" cy="9" r="5" />
    <path d="M13 13l3.5 3.5" />
  </>,
);
export const LibraryIcon = svg(
  <>
    <path d="M4 4v12" />
    <path d="M8 4v12" />
    <path d="M12 5l3.5 11" />
  </>,
);
export const SettingsIcon = svg(
  <>
    <circle cx="10" cy="10" r="2.5" />
    <path d="M10 3v2M10 15v2M3 10h2M15 10h2M5 5l1.5 1.5M13.5 13.5L15 15M15 5l-1.5 1.5M6.5 13.5L5 15" />
  </>,
);
/* An arrow into a tray: keep this book on the device. */
export const DownloadIcon = svg(
  <>
    <path d="M10 3v9" />
    <path d="M6.5 8.5L10 12l3.5-3.5" />
    <path d="M4 14v2h12v-2" />
  </>,
);
/* The same tray, filled: the book is already here. */
export const OfflineIcon = svg(
  <>
    <path d="M6 9.5l3 3 5.5-6" />
    <path d="M4 14v2h12v-2" />
  </>,
);
export const TrashIcon = svg(
  <>
    <path d="M4.5 5.5h11" />
    <path d="M8 5.5V4h4v1.5" />
    <path d="M6 5.5l.7 10h6.6l.7-10" />
  </>,
);
/* Two arrows chasing each other: a newer build is waiting to be taken. */
export const RefreshIcon = svg(
  <>
    <path d="M16 10a6 6 0 10-2 4.5" />
    <path d="M16 5.5V10h-4.5" />
  </>,
);

export const ChevronLeft = svg(<path d="M12 4l-5 6 5 6" />);
export const ChevronRight = svg(<path d="M8 4l5 6-5 6" />);
export const CloseIcon = svg(
  <>
    <path d="M5 5l10 10" />
    <path d="M15 5L5 15" />
  </>,
);

/**
 * The mark. Three spines, the third leaning the way a shelf leans when a book
 * has been taken off it.
 *
 * The same drawing as the favicon in `index.html`, path for path, because a
 * logo that is nearly the tab icon is worse than no logo: the eye reads the
 * difference as two products. It replaces the wordmark in the header, where a
 * phone has one line to spend and eighty pixels of "ReadCommons" bought
 * nothing a reader who is already inside the app did not know.
 *
 * Heavier than the icon grid's 1.5, because at 22px a 1.5 stroke in the accent
 * on a near black page reads as grey.
 */
export function Mark({ className = "h-5 w-5" }: Props) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M5 5v10" />
      <path d="M9 5v10" />
      <path d="M12.8 5.6l2.6 9.2" />
    </svg>
  );
}
